/**
 * Local services (§5.9.1): containers a workspace needs running on each
 * member's own machine — the database, the broker, the cache its code dials —
 * declared once in the workspace an organization shares and started by each
 * member's Airlock, on that member's click, with that member's container runner.
 *
 * Not Mode A. Mode A (`hostservice.ts`) opens one port of something already
 * running to a session; this says *what should be running* in the first place.
 * The two meet on the machine: a service started from here is a container like
 * any other, and Mode A's picker offers it like any other.
 *
 * Everything here is a declaration read by every member's machine, so it is
 * held narrow on purpose:
 *
 * - **An image and its ports, its environment, named volumes — nothing else.**
 *   No host path is ever mounted (a document written by somebody else would
 *   otherwise name a folder of this machine to hand to a container), no
 *   privileged flag, no network mode, no command line: the image's own
 *   entrypoint is what runs.
 * - **A secret is a reference, never a value.** An environment entry either
 *   holds a plain value — `POSTGRES_PASSWORD=postgres` for a throwaway dev
 *   database is a value a shared document may hold — or names a repository's
 *   variable (`from`), whose value each member already has, sealed in their own
 *   keychain by the workspace's secret paths (§5.25). A plain value shaped like
 *   a provider's key is refused (`looksLikeCredential`).
 * - **A container is found by its label**, not its name or its image:
 *   `app.airlock.service=<id>` is what says "this one is that declaration", and
 *   `app.airlock.spec` is a fingerprint of the declaration it was created from,
 *   so a declaration that changed reads as a container to recreate rather than
 *   one that is fine.
 */
import { looksLikeCredential } from '../workspace/secrets';

/** A dozen containers is a stack, not a dev setup; past it the cap says so. */
export const MAX_LOCAL_SERVICES = 12;
export const MAX_LOCAL_SERVICE_PORTS = 8;
export const MAX_LOCAL_SERVICE_ENV = 40;
export const MAX_LOCAL_SERVICE_VOLUMES = 4;
export const MAX_LOCAL_SERVICE_VALUE = 4096;
export const MAX_IMAGE_CHARS = 255;

/** The label every container Airlock creates carries, holding the declaration's id. */
export const SERVICE_LABEL = 'app.airlock.service';
/** The label holding the fingerprint of the declaration the container was created from. */
export const SPEC_LABEL = 'app.airlock.spec';

/** One published port: the machine's, then the container's. Published as `-p host:container`. */
export interface LocalServicePort {
  host: number;
  container: number;
}

export interface LocalServiceEnv {
  key: string;
  /** The value in the clear, or `null` exactly when `from` names where it is read. */
  value: string | null;
  /**
   * A repository's environment variable on the member's machine — the document
   * repository's id and the variable's key. Read at start by the member's app,
   * never written into the document (§5.25).
   */
  from: { repoId: string; key: string } | null;
}

/** A named volume, created by the runner and kept across recreations — never a host folder. */
export interface LocalServiceVolume {
  /** Its name within the service; the runner's volume is `localServiceVolumeName`. */
  name: string;
  /** Where the container sees it: an absolute POSIX path. */
  path: string;
}

export interface LocalService {
  id: string;
  /** A short machine name, what the container is called after: `postgres`, `redis`. */
  name: string;
  /** The OCI reference pulled and run: `postgres:16`, `ghcr.io/acme/mock-api:2.1`. */
  image: string;
  ports: LocalServicePort[];
  env: LocalServiceEnv[];
  volumes: LocalServiceVolume[];
}

const ID = /^[A-Za-z0-9_-]{1,40}$/;
const NAME = /^[a-z0-9][a-z0-9_.-]{0,39}$/;
const ENV_KEY = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
const REPO_ENV_KEY = /^[^\s/=]{1,200}$/;
/**
 * The names a declaration may not give a variable: the ones a process on the
 * host reads as an instruction rather than as data — the dynamic loaders'
 * (`LD_*`, `DYLD_*`), the container clients' (`DOCKER_*`, `PODMAN_*`,
 * `CONTAINERS_*`), git's (`GIT_*`, which name a program or a config to read), a
 * shell's and the search paths, and the ones that make an interpreter run a
 * file of the value's choosing; Python's by exception, since the switches kept
 * below only say how it prints. An agent's `workspace_fix` and Complete with
 * Agent are refused the same names (`actsOnHost`), and there the value does
 * reach a host process on a worktree placement (0253/workspace-fix-never-widens-reach).
 * The app hands a declared value to the container only
 * (an env file), so none of these reaches a host process today; the
 * declaration is refused anyway, here where both sides validate it, so a
 * document that names one says so to the person who wrote it rather than
 * waiting for the next way in (1080/local-service-env-container-only).
 */
const HOST_ENV_KEY =
  /^(?:LD_|DYLD_|DOCKER_|PODMAN_|CONTAINERS_|GIT_|BASH_FUNC_|NPM_CONFIG_|npm_config_)|^PYTHON(?!(?:UNBUFFERED|DONTWRITEBYTECODE|IOENCODING|UTF8|HASHSEED)$)|^(?:PATH|BASH_ENV|ENV|ZDOTDIR|SHELLOPTS|BASHOPTS|PROMPT_COMMAND|IFS|EDITOR|VISUAL|PAGER|MANPAGER|BROWSER|SSH_ASKPASS|NODE_OPTIONS|NODE_PATH|PERL5OPT|PERL5LIB|PERLLIB|RUBYOPT|RUBYLIB|RUSTC_WRAPPER|CC|CXX)$/;

/** Whether a host process reads this variable as an instruction rather than as data. */
export function actsOnHost(key: string): boolean {
  return HOST_ENV_KEY.test(key);
}

/** Whether a declaration may name a variable `key` — the shape, and none of `HOST_ENV_KEY`. */
export function isDeclarableEnvKey(key: string): boolean {
  return ENV_KEY.test(key) && !HOST_ENV_KEY.test(key);
}
/**
 * An OCI reference: an optional registry host (with a port), slash-separated
 * lowercase path components, an optional tag and an optional sha256 digest.
 * It starts with a letter or digit, so it can never be read as a flag.
 */
const IMAGE =
  /^(?:[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::[0-9]{1,5})?\/)?[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*(?:\/[a-z0-9]+(?:(?:[._]|__|-+)[a-z0-9]+)*)*(?::[A-Za-z0-9_][A-Za-z0-9_.-]{0,127})?(?:@sha256:[a-f0-9]{64})?$/;

export function isImageReference(value: string): boolean {
  return value.length <= MAX_IMAGE_CHARS && IMAGE.test(value);
}

export function isServiceName(value: string): boolean {
  return NAME.test(value);
}

function validPort(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 65535;
}

/** A container path a named volume may be mounted at: absolute, no `..`, nothing the `-v` grammar reads. */
export function isContainerPath(value: string): boolean {
  if (value.length < 2 || value.length > 200 || !value.startsWith('/')) return false;
  if (/[\s:,\\]/.test(value)) return false;
  return !value.split('/').some((segment) => segment === '..' || segment === '.');
}

/** What is wrong with one declaration, as one sentence — `null` when it can be kept. */
export function localServiceProblem(service: LocalService): string | null {
  if (!ID.test(service.id)) return 'The service has no usable id.';
  if (!NAME.test(service.name)) {
    return 'A name of lowercase letters, digits, dots, dashes or underscores, starting with a letter or digit — such as postgres.';
  }
  if (!isImageReference(service.image)) return `${service.name}: an image such as postgres:16 or ghcr.io/acme/api:2.1.`;
  if (service.ports.length > MAX_LOCAL_SERVICE_PORTS) return `${service.name}: ${MAX_LOCAL_SERVICE_PORTS} ports at most.`;
  const hostPorts = new Set<number>();
  for (const port of service.ports) {
    if (!validPort(port.host) || !validPort(port.container)) return `${service.name}: a port is a number from 1 to 65535.`;
    if (hostPorts.has(port.host)) return `${service.name}: port ${port.host} is published twice.`;
    hostPorts.add(port.host);
  }
  if (service.env.length > MAX_LOCAL_SERVICE_ENV) return `${service.name}: ${MAX_LOCAL_SERVICE_ENV} variables at most.`;
  const keys = new Set<string>();
  for (const entry of service.env) {
    if (!ENV_KEY.test(entry.key)) return `${service.name}: ${entry.key || 'a variable'} is not a variable name.`;
    if (!isDeclarableEnvKey(entry.key)) {
      return `${service.name}: ${entry.key} is a variable a program on this computer would act on, so a service cannot set it.`;
    }
    if (keys.has(entry.key)) return `${service.name}: ${entry.key} is set twice.`;
    keys.add(entry.key);
    if (entry.from) {
      if (entry.value !== null) return `${service.name}: ${entry.key} is either a value or a reference, not both.`;
      if (!ID.test(entry.from.repoId) || !REPO_ENV_KEY.test(entry.from.key)) {
        return `${service.name}: ${entry.key} names a repository variable that cannot exist.`;
      }
    } else {
      if (typeof entry.value !== 'string') return `${service.name}: ${entry.key} has no value.`;
      if (entry.value.length > MAX_LOCAL_SERVICE_VALUE || entry.value.includes('\0')) {
        return `${service.name}: ${entry.key} is too long a value.`;
      }
      if (looksLikeCredential(entry.value)) {
        return `${service.name}: ${entry.key} holds what looks like a real credential. Keep it in a repository's secret variable and reference that instead.`;
      }
    }
  }
  if (service.volumes.length > MAX_LOCAL_SERVICE_VOLUMES) {
    return `${service.name}: ${MAX_LOCAL_SERVICE_VOLUMES} volumes at most.`;
  }
  const volumes = new Set<string>();
  for (const volume of service.volumes) {
    if (!NAME.test(volume.name)) return `${service.name}: a volume name such as data.`;
    if (!isContainerPath(volume.path)) {
      return `${service.name}: a volume is mounted at an absolute path in the container, such as /var/lib/postgresql/data.`;
    }
    if (volumes.has(volume.name)) return `${service.name}: the volume ${volume.name} is declared twice.`;
    volumes.add(volume.name);
  }
  return null;
}

/**
 * What a whole list may not do that each entry may: two services of one name
 * (one container name), or two publishing one port of the machine (only one of
 * them could ever start).
 */
export function localServicesProblem(services: readonly LocalService[]): string | null {
  if (services.length > MAX_LOCAL_SERVICES) return `A workspace declares ${MAX_LOCAL_SERVICES} local services at most.`;
  const names = new Set<string>();
  const ids = new Set<string>();
  const ports = new Map<number, string>();
  for (const service of services) {
    const own = localServiceProblem(service);
    if (own) return own;
    if (ids.has(service.id)) return 'Two services share one id.';
    ids.add(service.id);
    if (names.has(service.name)) return `Two services are called ${service.name}.`;
    names.add(service.name);
    for (const port of service.ports) {
      const holder = ports.get(port.host);
      if (holder) return `${holder} and ${service.name} both publish port ${port.host} of the computer.`;
      ports.set(port.host, service.name);
    }
  }
  return null;
}

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** An entry coerced to the shape, nothing checked yet — what both the clamp and the refusal read. */
function shaped(value: unknown): LocalService | null {
  const raw = object(value);
  if (!raw) return null;
  const list = (input: unknown): unknown[] => (Array.isArray(input) ? input : []);
  const service: LocalService = {
    id: typeof raw.id === 'string' ? raw.id : '',
    name: typeof raw.name === 'string' ? raw.name.trim() : '',
    image: typeof raw.image === 'string' ? raw.image.trim() : '',
    ports: list(raw.ports).map((entry) => {
      const port = object(entry) ?? {};
      return { host: port.host as number, container: port.container as number };
    }),
    env: list(raw.env).map((entry) => {
      const env = object(entry) ?? {};
      const from = object(env.from);
      return {
        key: typeof env.key === 'string' ? env.key.trim() : '',
        value: typeof env.value === 'string' ? env.value : null,
        from: from ? { repoId: String(from.repoId ?? ''), key: String(from.key ?? '') } : null
      };
    }),
    volumes: list(raw.volumes).map((entry) => {
      const volume = object(entry) ?? {};
      return { name: String(volume.name ?? '').trim(), path: String(volume.path ?? '').trim() };
    })
  };
  return service;
}

function oneService(value: unknown): LocalService | null {
  const service = shaped(value);
  return service && !localServiceProblem(service) ? service : null;
}

/**
 * Why a list read from a network would not be kept whole, as the sentence the
 * account area shows beside its field — `null` when every entry passes. The
 * reader of a document calls it before comparing what the clamp kept, so a
 * refusal names its cause rather than "not a value Airlock keeps".
 */
export function localServicesInputProblem(value: unknown): string | null {
  if (!Array.isArray(value)) return 'Expected a list.';
  const services: LocalService[] = [];
  for (const entry of value) {
    const service = shaped(entry);
    if (!service) return 'Each local service is an object.';
    services.push(service);
  }
  return localServicesProblem(services);
}

/**
 * The stored list, clamped like every other stored value: an entry that fails
 * `localServiceProblem`, or repeats an id, a name or a published port already
 * kept, is dropped rather than repaired — what it would start is not a thing
 * to guess at. The account area and the document reader refuse instead
 * (`parse.ts` compares what this keeps with what it was given).
 */
export function localServices(value: unknown): LocalService[] {
  if (!Array.isArray(value)) return [];
  const out: LocalService[] = [];
  for (const entry of value) {
    const service = oneService(entry);
    if (!service) continue;
    if (localServicesProblem([...out, service])) continue;
    out.push(service);
    if (out.length >= MAX_LOCAL_SERVICES) break;
  }
  return out;
}

/** The container a declaration runs as on a member's machine — the name the person sees in their runner. */
export function localServiceContainer(service: Pick<LocalService, 'id' | 'name'>): string {
  return `airlock-${service.name}-${service.id}`;
}

/** A named volume of a declaration, as the runner names it: kept with the service, never shared with another. */
export function localServiceVolumeName(service: Pick<LocalService, 'id' | 'name'>, volume: string): string {
  return `airlock-${service.name}-${service.id}-${volume}`;
}

/**
 * A fingerprint of what the container is created from — the image, the ports,
 * the variables (a reference by what it names, not by its value), the volumes.
 * FNV-1a over the canonical JSON: a label to compare, not a secret, so nothing
 * stronger is needed and nothing outside the language is reached for.
 */
export function localServiceSpec(service: LocalService): string {
  const canonical = JSON.stringify([
    service.image,
    service.ports.map((port) => [port.host, port.container]),
    service.env.map((entry) => [entry.key, entry.value, entry.from ? [entry.from.repoId, entry.from.key] : null]),
    service.volumes.map((volume) => [volume.name, volume.path])
  ]);
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i += 1) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
